import React, { useState, useCallback, useRef, useEffect, ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AuthResponse } from '../../types/api/auth';
import { authWithUsernamePassword } from '../../services/auth';
import { useAlert } from '../../context/alert';
import { TwoFactorAuthForm } from './TwoFactorAuthForm';
import ModalSheet from '../basic/ModalSheet';
import { FormControl, TextInput, Button, Stack, Text } from '@primer/react'
import { EyeIcon, EyeClosedIcon } from '@primer/octicons-react'

export const SignInLoginPasswordForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [is2FASent, setIs2FASent] = useState(false);
    const [authData, setAuthData] = useState<AuthResponse | undefined>(undefined);
    const { setError: setAlertError } = useAlert();

    const [isOpenEnterCodeModal, setIsOpenEnterCodeModal] = React.useState(false)

    const { mutate: authenticate, isPending } = useMutation({
        mutationFn: authWithUsernamePassword,
        onSuccess: (data) => {
            if (data) {
                setAuthData(data);
                setIs2FASent(true);
            }
        },
        onError: (error: any) => {
            switch (error.status) {
                case 404:
                case 400:
                    setError('Error'); // Для выделения полей красным
                    setAlertError('Для восстановления пароля обратитесь к администратору', 'Вы ввели неправильный логин и/или пароль. ');
                    break;
                default:
                    setAlertError(error.message);
            }
        }
    });

    const handleUsernameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setUsername(e.target.value);
        setError(prevError => prevError ? '' : prevError);
    }, []);

    const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        setError(prevError => prevError ? '' : prevError);
    }, []);

    const submit = useCallback((event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        if (username && password && username.trim() && password.trim()) {
            authenticate({ username, password });
        } else {
            setAlertError('Введите логин и пароль');
        }
    }, [username, password, authenticate, setAlertError]);

    useEffect(() => {
        if (authData && is2FASent) {
            setIsOpenEnterCodeModal(true)
        }
    }, [authData, is2FASent])

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const updateNewTempToken = () => {
        if (username && password && username.trim() && password.trim()) {
            authenticate({ username, password });
        }
    }

    const subtitle = `Введите логин и пароль для входа 
    в админ-панель сервиса`;

    /*
   Используется FormControl только для того что бы рамка вокруг поля 
   могла становится красной по ошибке
   */
    return (
        <main className="relative min-h-screen overflow-hidden">
            <ModalSheet
                isOpen={isOpenEnterCodeModal}
                onClose={() => setIsOpenEnterCodeModal(false)}
                title="Двухфакторная авторизация"
                size="small"
                headerSize="regular"
            >
                {({ setTitle }) => (
                    authData && (
                        <TwoFactorAuthForm
                            authData={authData}
                            onNeedUpdateNewTempToken={updateNewTempToken}
                            onDismiss={() => setIsOpenEnterCodeModal(false)}
                            setTitle={setTitle}
                        />
                    )
                )}
            </ModalSheet>
            <Stack className="mt-8 relative z-9">
                <Stack align="center" direction="horizontal" gap='none'>
                    <img src="/icons/logo.png" alt="logo" className="w-[33.6px] aspect-square" />
                    <span className="ml-[9.6px] text-[24px] text-aloix-black font-medium">Aloix</span>
                </Stack>
                <div
                    className="flex justify-center items-center mt-[-92px]"
                    style={{ height: 'calc(100vh - 100px)' }}
                >
                    <Stack gap='normal'>
                        <Stack gap='none'>
                            <Text as="span" weight='medium' className='text-[32px] text-aloix-black'>Вход</Text>
                            <Text as="span" weight='normal' className='text-[14px] text-aloix-grey whitespace-pre-line'>{subtitle}</Text>
                        </Stack>
                        <form onSubmit={submit}>
                            <Stack gap='normal'>
                                <FormControl required>
                                    <FormControl.Label className='invisible h-0'></FormControl.Label>
                                    <TextInput
                                        value={username}
                                        type='text'
                                        placeholder='Логин'
                                        size="large"
                                        contrast={!!(username && username.trim())}
                                        className='w-full'
                                        onChange={handleUsernameChange}
                                    />
                                    {error && (
                                        <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
                                    )}
                                </FormControl>
                                <FormControl required>
                                    <FormControl.Label className='invisible h-0'></FormControl.Label>
                                    <TextInput
                                        value={password}
                                        type={showPassword ? "text" : "password"}
                                        placeholder='Пароль'
                                        size="large"
                                        contrast={!!(password && password.trim())}
                                        className='w-full'
                                        onChange={handlePasswordChange}
                                        trailingAction={
                                            <TextInput.Action
                                                onClick={togglePasswordVisibility}
                                                icon={showPassword ? EyeClosedIcon : EyeIcon}
                                                aria-label="Показать/скрыть"
                                            />}
                                    />
                                    {error && (
                                        <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
                                    )}
                                </FormControl>
                                <Button
                                    className='w-full'
                                    type="submit"
                                    variant="primary"
                                    size="large"
                                    loading={isPending}>
                                    Войти
                                </Button>
                            </Stack>
                        </form>
                    </Stack>
                </div>
            </Stack>
            <img
                src="/icons/pattern.png"
                alt="pattern"
                className="fixed bottom-0 left-0 w-screen pointer-events-none object-cover"
                style={{ zIndex: 1, maxHeight: '20vh' }}
            />
        </main>
    );
};